import React from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
// components
import type { MenuProps } from '@mui/material/Menu';
import MuiMenu from '@mui/material/Menu';
import type { SwipeableDrawerProps } from '@mui/material/SwipeableDrawer';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import ListItemIcon from '@mui/material/ListItemIcon';
import MuiMenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import type { CircularProgressProps } from '@mui/material/CircularProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CrossIcon from 'components/vectors/CrossIcon';
import { match } from 'ts-pattern';
import Divider from '@mui/material/Divider';
import type { PaperProps } from '@mui/material/Paper';

export type MenuItemType =
  | {
      title: string;
      icon?: React.ReactNode;
      onClick: () => void;
      hidden?: boolean;
      loading?: boolean;
      disabled?: boolean;
      color?: CircularProgressProps['color'];
      textColor?: string;
    }
  | {
      divider: true;
      hidden?: boolean;
    };

interface Props extends React.PropsWithChildren {
  menuItems?: MenuItemType[];
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  className?: string;
  anchorOrigin?: MenuProps['anchorOrigin'];
  transformOrigin?: MenuProps['transformOrigin'];
  title: string;
  preventMobileDrawer?: boolean;
  paperProps?: PaperProps;
  MenuListProps?: MenuProps['MenuListProps'];
  TransitionProps?: MenuProps['TransitionProps'] & SwipeableDrawerProps['SlideProps'];
}

/**
 * Render menu for desktop and drawer for mobile
 */
export default function Menu(props: Props) {
  const {
    title,
    menuItems,
    anchorEl,
    onClose,
    open,
    anchorOrigin,
    transformOrigin,
    children,
    preventMobileDrawer = false,
    paperProps,
    MenuListProps,
    TransitionProps,
  } = props;
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  if (isMobile && !preventMobileDrawer) {
    return (
      <SwipeableDrawer
        disableSwipeToOpen
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        PaperProps={{
          ...paperProps,
          sx: {
            p: '0px',
            borderRadius: '10px 10px 0 0 ',
            overflow: 'hidden',
            ...paperProps?.sx,
          },
        }}
        SlideProps={TransitionProps}
      >
        <Stack
          py="18px"
          px={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Typography fontWeight="semibold">{title}</Typography>
          <IconButton onClick={onClose} sx={{ my: '-4px', mr: '-4px' }}>
            <CrossIcon />
          </IconButton>
        </Stack>
        {children || (
          <List sx={{ px: 1, pt: '12px', pb: 4 }}>
            {menuItems?.map((item, index) =>
              match(item)
                .with({ divider: true }, () => (item.hidden ? null : <Divider key={index} />))
                .otherwise((_item) =>
                  _item.hidden ? null : (
                    <ListItemButton
                      key={index}
                      onClick={_item.onClick}
                      disabled={_item.disabled || _item.loading}
                    >
                      {_item.icon || _item.loading ? (
                        <ListItemIcon>
                          {_item.loading ? (
                            <CircularProgress color={_item.color} size="24px" />
                          ) : (
                            _item.icon
                          )}
                        </ListItemIcon>
                      ) : null}
                      <Typography color={_item.textColor}>{_item.title}</Typography>
                    </ListItemButton>
                  )
                )
            )}
          </List>
        )}
      </SwipeableDrawer>
    );
  }

  return (
    <MuiMenu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{ paper: paperProps }}
      MenuListProps={MenuListProps}
      TransitionProps={TransitionProps}
    >
      {children ||
        menuItems?.map((item, index) =>
          match(item)
            .with({ divider: true }, () => (item.hidden ? null : <Divider key={index} />))
            .otherwise((_item) =>
              _item.hidden ? null : (
                <MuiMenuItem
                  key={index}
                  onClick={_item.onClick}
                  disabled={_item.disabled || _item.loading}
                >
                  {(_item.icon || _item.loading) && (
                    <ListItemIcon>
                      {_item.loading ? (
                        <CircularProgress color={_item.color} size="20px" />
                      ) : (
                        _item.icon
                      )}
                    </ListItemIcon>
                  )}
                  <Typography variant="body2" lineHeight="20px" color={_item.textColor}>
                    {_item.title}
                  </Typography>
                </MuiMenuItem>
              )
            )
        )}
    </MuiMenu>
  );
}
